import { apiBaseUrl } from '../../backend/config.js';
import { connectWebSocket, socket, messageQueue } from './socketU.js';
import { getAuthToken } from './auth.js';
import { showNotification, updateTotalPointsDisplay, showPopupPoints, clearLEDDisplay } from './notif.js';
import { t, confirmT, updateElementText } from './translationHelper.js';


function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
}

function getPortfolioId() {
  return getCookie('portfolioId') || localStorage.getItem('portfolioId');
}

export async function fetchBondData(bondId) {
  try {
	const response = await fetch(`${apiBaseUrl}/api/bonds/${bondId}`, {
	  headers: {
		'Authorization': `Bearer ${getAuthToken()}`,
		'Content-Type': 'application/json',
	  },
	});

	console.log('Response Status:', response.status);

	const contentType = response.headers.get('content-type');
	if (contentType && contentType.includes('text/html')) {
	  // Handle unexpected HTML response (e.g., login page)
	  console.error('Unexpected HTML response. Redirecting to login...');
	  window.location.href = '/login.html';
	  return null;
	}

	if (!response.ok) {
	  const errorData = await response.json();
	  throw new Error(errorData.error || 'Failed to fetch bond data');
	}

	return await response.json();
  } catch (error) {
	console.error('Error fetching bond data:', error);
	return null;
  }
}

export async function placeBondBid(bondId, amount, targetUser) {
  if (!bondId || typeof bondId !== 'string') {
	console.error('Invalid bondId provided:', bondId);
	throw new Error('Invalid bondId.');
  }

  if (!amount || isNaN(amount) || amount <= 0) {
	console.error('Invalid bid amount provided:', amount);
	throw new Error('Invalid bid amount.');
  }

  if (!targetUser || typeof targetUser !== 'string') {
	console.error('Invalid targetUser provided:', targetUser);
	throw new Error('Invalid targetUser.');
  }

  try {
	const response = await fetch(`${apiBaseUrl}/api/bonds/${bondId}/bids`, {
	  method: 'POST',
	  headers: {
		'Authorization': `Bearer ${getAuthToken()}`,
		'Content-Type': 'application/json',
	  },
	  body: JSON.stringify({ amount, targetUser }),
	});

	if (!response.ok) {
	  const errorData = await response.json();
	  throw new Error(errorData.error || 'Failed to place bid.');
	}

	console.log('Bid placed successfully!');
  } catch (error) {
	console.error('Error placing bond bid:', error.message);
	throw error;
  }
}


export async function buyBond(bondId) {
  try {
	console.log('Initiating bond purchase for ID:', bondId);

	// Retrieve buyer's portfolioId from localStorage
	const buyerPortfolioId = localStorage.getItem('portfolioId');
	console.log('Retrieved buyerPortfolioId from localStorage:', buyerPortfolioId);

	// Throw an error if portfolioId is missing
	if (!buyerPortfolioId) {
	  throw new Error('Portfolio ID is missing in localStorage');
	}

	// Send portfolioId along with the request
	const payload = JSON.stringify({ portfolioId: buyerPortfolioId });
	console.log('Payload sent to backend:', payload);

	const response = await fetch(`${apiBaseUrl}/api/bonds/buy/${bondId}`, {
	  method: 'POST',
	  headers: {
		'Authorization': `Bearer ${getAuthToken()}`,
		'Content-Type': 'application/json',
	  },
	  body: payload,
	});

	if (!response.ok) {
	  const errorData = await response.json();
	  console.error('API error response:', errorData);
	  throw new Error(errorData.error || 'Failed to buy bond');
	}

	const data = await response.json();
	console.log('Bond purchase response data:', data);
	return data;
  } catch (error) {
	console.error('Error buying bond:', error);
	throw error;
  }
}


export function showBondBidPopup(bidData, portfolioId) {
  const yieldDescriptor = getQualitativeDescriptor(bidData.zollarValue || 0);

  // Create and populate the popup
  const popup = document.createElement('div');
  popup.id = 'BondPop-popup-container';

  popup.innerHTML = `
	<div class="BondPop-popup-content">
	  <h2>Bond Bid Offer</h2>
	  <h3><strong>${bidData.portfolioName} offers to buy "${bidData.bondName}" for ${bidData.amount} points.</strong></h3>
	  <p><strong>Interest Rate:</strong> ${bidData.interestRate}%</p>
	  <p><strong>Yield:</strong> ${yieldDescriptor} Zollars</p>
	  <p><strong>Maturity Date:</strong> ${new Date(bidData.maturityDate).toLocaleString()}</p>
	  <p><strong>Current Price:</strong> ${bidData.price || 'N/A'}</p>
	  <div class="BondPop-buttons">
		<button id="BondPop-acceptOffer">Accept</button>
		<button id="BondPop-declineOffer">Decline</button>
	  </div>
	</div>
  `;

  document.body.appendChild(popup);

  // Event listener for accepting the bid
  document.getElementById('BondPop-acceptOffer').onclick = async () => {
	const { bondId, bidderId } = bidData;
	const bondBidId = bidData._id;
	const portfolioId = getPortfolioId();
	const token = localStorage.getItem('token');
 
   try {
	 console.log('Accepting bond bid with data:', { bondId, bondBidId, portfolioId, bidderId });
 
	 // Include bidderId in the payload
	 const response = await fetch(`/api/bonds/${bondId}/bids/accept`, {
	   method: 'POST',
	   headers: {
		 'Content-Type': 'application/json',
		 Authorization: `Bearer ${localStorage.getItem('token')}`, // Ensure token is passed
	   },
	   body: JSON.stringify({ bondBidId, portfolioId, bidderId }), // Add bidderId here
	 });
 
	 const result = await response.json();
 
	 if (response.ok) {
	   showNotification('Bid accepted successfully.', true);
 
	   // Emit the bondBidAccepted event to notify the bidder
	   socket.emit('bondBidAccepted', {
		 bondId,
		 bidId: bondBidId,
		 bondName: bidData.bondName,
		 bidderId, // Pass bidderId here as well
	   });
	 } else {
	   console.error('Failed to accept bond bid:', result.error);
	   showNotification('Error accepting bid: ' + result.error, false);
	 }
   } catch (error) {
	 console.error('Network error:', error.message);
	 showNotification('Network error occurred while accepting bond bid.', false);
   }
 
   popup.remove();
 };

  // Event listener for declining the bid
  document.getElementById('BondPop-declineOffer').onclick = async () => {
  	const { bondId, bidderId } = bidData;
	const bondBidId = bidData._id;
	const portfolioId = getPortfolioId();
	const token = localStorage.getItem('token');

	try {
	  const response = await fetch(`/api/bonds/${bondId}/bids/decline`, {
		method: 'POST',
		headers: {
		  'Content-Type': 'application/json',
		  Authorization: `Bearer ${localStorage.getItem('token')}`,
		},
		body: JSON.stringify({ bondBidId, portfolioId }),
	  });

	  const result = await response.json();

	  if (response.ok) {
		showNotification('Bid declined successfully.', false);
		socket.emit('bondBidDeclined', {
		  bondId,
		  bidId: bondBidId,
		  bondName: bidData.bondName,
		  bidderId,
		}); // Notify the initiator
	  } else {
		console.error('Failed to decline bid:', result.error);
		showNotification('Error declining bid: ' + result.error, false);
	  }
	} catch (error) {
	  console.error('Network error:', error.message);
	  showNotification('Network error occurred while declining bid.', false);
	}

	popup.remove();
  };
}



	function getQualitativeDescriptor(quantity) {
	if (quantity === 0) {
		return 'None';
	} else if (quantity === 1) { // Use strict equality
		return 'Little';
	} else if (quantity >= 2 && quantity <= 5) {
		return 'A Few';
	} else if (quantity >= 6 && quantity <= 10) {
		return 'Some';
	} else if (quantity >= 11 && quantity <= 15) {
		return 'Many';
	} else {
		return 'A Huge Amount';
	}
}


function capitalizeFirstLetter(string) {
	return string.charAt(0).toUpperCase() + string.slice(1);
}